/**
 * Local opt-in preference for the background timer notifications that the
 * Service Worker fires when the tab is hidden. Stored in localStorage; default
 * is OFF (opt-in by design).
 *
 * Cross-component / cross-tab updates broadcast a `storage` event (native, for
 * other tabs) and a `timer-notifications-changed` CustomEvent (in-tab, so
 * subscribers don't have to round-trip through storage).
 */

const STORAGE_KEY = 'climb_log.timer_notifications_enabled.v1'
const CUSTOM_EVENT = 'timer-notifications-changed'

export function getTimerNotificationsEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export function setTimerNotificationsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false')
  } catch {
    /* non-fatal */
  }
  window.dispatchEvent(new CustomEvent(CUSTOM_EVENT, { detail: enabled }))
}

export function subscribeToTimerNotifications(listener: (enabled: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener(e.newValue === 'true')
  }
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent<boolean>).detail
    listener(Boolean(detail))
  }
  window.addEventListener('storage', onStorage)
  window.addEventListener(CUSTOM_EVENT, onCustom as EventListener)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(CUSTOM_EVENT, onCustom as EventListener)
  }
}
